import { downloadQueue } from "./downloadQueue";

const tryTrackDownload = async (
  signedUrl,
  versionType,
  versionId,
  isBlockedDownload,
  refreshUserDownloads,
  showToast,
  trackId,
  trackTitle,
  editId,
  editTitle,
  remixerId,
  doCheckDownloadEligibility,
  doIncreaseDownloadCount,
  doDecreaseDownloadCount,
  downloadLimitMessage,
  onClose,
  optimisticIncrementDownload,
  optimisticDecrementDownload,
) => {
  if (isBlockedDownload) {
    showToast(
      "error",
      "To avoid frequent download attempts, please wait a few seconds before trying again.",
    );
    if (optimisticDecrementDownload) {
      optimisticDecrementDownload(versionId);
    }
    return;
  }

  if (!trackId || !editId || !versionId) {
    console.log(trackId, editId, versionId);
    showToast("error", "This track is not available for download.");
    if (optimisticDecrementDownload) {
      optimisticDecrementDownload(versionId);
    }
    return;
  }

  const result = await doCheckDownloadEligibility({
    trackId: trackId,
    editId: editId,
    remixerId: remixerId ? remixerId : null,
    versionId: versionId,
  });

  if (!signedUrl) {
    showToast("error", "Sorry, no audio file URL is available for this track.");
    if (optimisticDecrementDownload) {
      optimisticDecrementDownload(versionId);
    }
    return;
  }

  if (
    !(
      result &&
      result?.success &&
      result?.response?.status === "success" &&
      result?.response?.eligibility &&
      result?.response?.downloadCount <= 2
    )
  ) {
    if (optimisticDecrementDownload) {
      optimisticDecrementDownload(versionId);
    }

    // Handle daily download limit (HTTP 429)
    if (
      result?.error?.code === "429" ||
      result?.error?.name === "DOWNLOAD_LIMIT_REACHED"
    ) {
      showToast(
        "warning",
        downloadLimitMessage ||
          result?.error?.message ||
          "You have reached your daily download limit. Please try again tomorrow.",
      );
      return;
    }

    let errorMessage = "";
    if (!result?.error.message) {
      errorMessage = "An error occurred while checking download eligibility.";
    } else {
      errorMessage =
        result?.error.message ||
        "An error occurred while checking download eligibility.";
    }

    showToast("error", errorMessage);
    return;
  }

  try {
    // Append a unique hash fragment to prevent browser caching/deduplication of identical URLs
    const uniqueUrl = signedUrl.includes("#")
      ? signedUrl
      : `${signedUrl}#t=${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    // Verify the URL is valid and authorized before triggering download
    const controller = new AbortController();
    try {
      const response = await fetch(uniqueUrl, { signal: controller.signal });
      if (!response.ok) {
        controller.abort();
        throw new Error(`Unauthorized or invalid signature (HTTP ${response.status})`);
      }
      controller.abort(); // Cancel the rest of the stream since headers are ok
    } catch (fetchError) {
      console.error("[tryTrackDownload] Verification fetch failed:", fetchError);
      throw new Error("Download verification failed. You may be unauthorized or the signature is invalid.");
    }

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = uniqueUrl;
    document.body.appendChild(iframe);

    // Clean up the iframe after 10 seconds to keep DOM clean
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 10000);

    // Sync download count from server if the queue is about to become idle
    if (trackId && versionId) {
      const stats = downloadQueue.getStats();
      const isLastItem = stats.running <= 1 && stats.pending === 0;
      if (isLastItem) {
        setTimeout(async () => {
          await refreshUserDownloads();
        }, 1500);
      }
    }

    onClose();
  } catch (error) {
    await doDecreaseDownloadCount({
      trackId: trackId,
      editId: editId,
      remixerId: remixerId ? remixerId : null,
      versionId: versionId,
    });

    console.error("Download failed:", error);
    showToast("error", `Failed to download "${trackTitle}".`);
    onClose();

    // Revert optimistic count
    if (optimisticDecrementDownload) {
      optimisticDecrementDownload(versionId);
    }
    
    // Only refresh user downloads if queue is idle
    const stats = downloadQueue.getStats();
    const isLastItem = stats.running <= 1 && stats.pending === 0;
    if (isLastItem) {
      setTimeout(() => {
        refreshUserDownloads();
      }, 1500);
    }
  }
};

export default tryTrackDownload;
